/**
 * TikTok 핸들 검증을 위한 도메인 로직
 * 비즈니스 규칙과 제약사항을 순수 함수로 정의
 */

export interface TikTokHandleValidationResult {
  valid: boolean;
  error?: string;
  normalizedHandle?: string;
}

// biome-ignore lint/complexity/noStaticOnlyClass: intentional design for namespace-like grouping
export class TikTokHandleValidator {
  private static readonly MIN_LENGTH = 2;
  private static readonly MAX_LENGTH = 24;
  private static readonly VALID_PATTERN = /^[a-zA-Z0-9_.]+$/;

  /**
   * TikTok 핸들의 유효성을 검증하고 정규화합니다.
   */
  static validate(handle: string): TikTokHandleValidationResult {
    const trimmedHandle = handle.trim();

    if (!trimmedHandle) {
      return {
        valid: false,
        error: '',
      };
    }

    // @ 제거하고 정규화
    const normalizedHandle = TikTokHandleValidator.normalizeHandle(trimmedHandle);

    // 길이 검증
    if (
      normalizedHandle.length < TikTokHandleValidator.MIN_LENGTH ||
      normalizedHandle.length > TikTokHandleValidator.MAX_LENGTH
    ) {
      return {
        valid: false,
        error: 'TikTok handle must be between 2 and 24 characters.',
      };
    }

    // 패턴 검증
    if (!TikTokHandleValidator.VALID_PATTERN.test(normalizedHandle)) {
      return {
        valid: false,
        error: 'Only letters, numbers, underscores, and periods are allowed.',
      };
    }

    // 마침표로 끝나는지 검증
    if (normalizedHandle.endsWith('.')) {
      return {
        valid: false,
        error: 'Username cannot end with a period.',
      };
    }

    return {
      valid: true,
      normalizedHandle,
    };
  }

  /**
   * 다양한 입력 형태를 정규화합니다.
   * - tiktok.com/@username -> username
   * - @username -> username
   * - username -> username
   */
  static normalizeHandle(input: string): string {
    const trimmed = input.trim();

    // tiktok.com/@username 형태 처리
    const tiktokUrlMatch = trimmed.match(/tiktok\.com\/@(.+)$/);
    if (tiktokUrlMatch) {
      return tiktokUrlMatch[1];
    }

    // @username 형태 처리
    if (trimmed.startsWith('@')) {
      return trimmed.substring(1);
    }

    // 일반 username
    return trimmed;
  }

  /**
   * TikTok 프로필 URL용 핸들 생성 (@ 접두사 포함)
   */
  static toTikTokHandle(normalizedHandle: string): string {
    return normalizedHandle.startsWith('@') ? normalizedHandle : `@${normalizedHandle}`;
  }
}
